import { useEffect, useState } from "react"
import {
  listRootMarketCategoriesApi,
  listMarketCategoryChildrenApi,
  createMarketCategoryApi,
  deleteMarketCategoryApi,
  MarketCategory,
} from "../../features/china-markets/api/market-categories.api"
import { PlusIcon, TrashIcon, TagIcon, ChevronRightIcon } from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

type Props = {
  open: boolean
  onClose: () => void
}

type Row = MarketCategory & { children?: MarketCategory[]; expanded?: boolean; loadingChildren?: boolean }

export const MarketCategoriesModal = ({ open, onClose }: Props) => {
  const [roots, setRoots] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)

  const [codename, setCodename] = useState("")
  const [parentId, setParentId] = useState<string>("")
  const [order, setOrder] = useState<number>(0)
  const [saving, setSaving] = useState(false)

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadRoots = () => {
    setLoading(true)
    listRootMarketCategoriesApi()
      .then((res) => setRoots(res.data.items.map((c) => ({ ...c }))))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (open) loadRoots()
  }, [open])

  const toggleExpand = async (root: Row) => {
    if (root.expanded) {
      setRoots((prev) => prev.map((r) => (r.id === root.id ? { ...r, expanded: false } : r)))
      return
    }
    if (root.children) {
      setRoots((prev) => prev.map((r) => (r.id === root.id ? { ...r, expanded: true } : r)))
      return
    }
    setRoots((prev) => prev.map((r) => (r.id === root.id ? { ...r, loadingChildren: true } : r)))
    const res = await listMarketCategoryChildrenApi(root.id)
    setRoots((prev) =>
      prev.map((r) => (r.id === root.id ? { ...r, children: res.data.items, expanded: true, loadingChildren: false } : r))
    )
  }

  const handleCreate = async () => {
    if (!codename.trim()) return
    setSaving(true)
    try {
      await createMarketCategoryApi({
        codename: codename.trim(),
        parent_id: parentId || null,
        order,
      })
      setCodename("")
      setOrder(0)
      if (parentId) {
        const parent = roots.find((r) => r.id === parentId)
        if (parent) {
          const res = await listMarketCategoryChildrenApi(parentId)
          setRoots((prev) =>
            prev.map((r) => (r.id === parentId ? { ...r, children: res.data.items, expanded: true, has_children: true } : r))
          )
        }
      } else {
        loadRoots()
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: MarketCategory, parent?: Row) => {
    setDeleting(true)
    try {
      await deleteMarketCategoryApi(row.id)
      if (parent) {
        setRoots((prev) =>
          prev.map((r) => (r.id === parent.id ? { ...r, children: (r.children ?? []).filter((c) => c.id !== row.id) } : r))
        )
      } else {
        setRoots((prev) => prev.filter((r) => r.id !== row.id))
      }
      setConfirmDeleteId(null)
    } finally {
      setDeleting(false)
    }
  }

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <TagIcon className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-800">Категории рынков</h2>
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center">
              <XMarkIcon className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-1">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-9 bg-gray-100 rounded-lg animate-pulse" />
              ))
            ) : roots.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Категорий пока нет</p>
            ) : (
              roots.map((root) => (
                <div key={root.id}>
                  <div className="flex items-center gap-2 group py-1.5">
                    <button
                      onClick={() => toggleExpand(root)}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0"
                    >
                      <ChevronRightIcon className={`w-3.5 h-3.5 transition-transform ${root.expanded ? "rotate-90" : ""}`} />
                    </button>
                    <span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md">{root.codename}</span>
                    <span className="text-[11px] text-gray-300">order {root.order}</span>
                    <div className="ml-auto">
                      {confirmDeleteId === root.id ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            disabled={deleting}
                            onClick={() => handleDelete(root)}
                            className="text-[11px] font-medium text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded-md disabled:opacity-40"
                          >
                            Удалить
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-[11px] font-medium text-gray-500 hover:text-gray-700 px-2 py-1"
                          >
                            Отмена
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(root.id)}
                          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-colors p-1"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {root.expanded && (
                    <div className="ml-7 pl-3 border-l border-gray-100 space-y-1 py-1">
                      {root.loadingChildren ? (
                        <div className="h-6 bg-gray-100 rounded animate-pulse w-32" />
                      ) : root.children && root.children.length > 0 ? (
                        root.children.map((child) => (
                          <div key={child.id} className="flex items-center gap-2 group py-1">
                            <span className="font-mono text-xs bg-gray-50 text-gray-500 px-2 py-1 rounded-md">{child.codename}</span>
                            <span className="text-[11px] text-gray-300">order {child.order}</span>
                            <div className="ml-auto">
                              {confirmDeleteId === child.id ? (
                                <div className="flex items-center gap-1.5">
                                  <button
                                    disabled={deleting}
                                    onClick={() => handleDelete(child, root)}
                                    className="text-[11px] font-medium text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded-md disabled:opacity-40"
                                  >
                                    Удалить
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeleteId(null)}
                                    className="text-[11px] font-medium text-gray-500 hover:text-gray-700 px-2 py-1"
                                  >
                                    Отмена
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setConfirmDeleteId(child.id)}
                                  className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-colors p-1"
                                >
                                  <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-[11px] text-gray-300 py-1">Нет дочерних категорий</p>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Add form */}
          <div className="px-6 py-4 border-t bg-gray-50/60 flex-shrink-0 space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Новая категория</p>
            <div className="flex gap-2">
              <input
                value={codename}
                onChange={(e) => setCodename(e.target.value)}
                placeholder="codename"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Корень</option>
                {roots.map((r) => (
                  <option key={r.id} value={r.id}>{r.codename}</option>
                ))}
              </select>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(Number(e.target.value))}
                placeholder="order"
                className="w-16 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                disabled={saving || !codename.trim()}
                onClick={handleCreate}
                className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {saving
                  ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <PlusIcon className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}